<script setup lang="ts">
import emblaCarouselVue from 'embla-carousel-vue'
import type { Product } from '~~/shared/types/store'

const props = defineProps<{
  products: Product[]
  itemListId?: string
  itemListName?: string
}>()

const [emblaRef, emblaApi] = emblaCarouselVue({
  align: 'start',
  containScroll: 'trimSnaps',
  dragFree: false,
  skipSnaps: true,
})

watch(() => props.products, () => {
  nextTick(() => emblaApi.value?.reInit())
})

function scrollByPage(direction: 'prev' | 'next') {
  if (direction === 'next') {
    emblaApi.value?.scrollNext()
  }
  else {
    emblaApi.value?.scrollPrev()
  }
}
</script>

<template>
  <div class="product-scroll-carousel carousel-outer">
    <button
      class="carousel-arrow carousel-arrow--prev"
      type="button"
      aria-label="Poprzednie"
      @click="scrollByPage('prev')"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <polyline points="15 18 9 12 15 6" />
      </svg>
    </button>

    <div ref="emblaRef" class="product-carousel__viewport" tabindex="0" aria-label="Karuzela produktów" @dragstart.prevent>
      <div class="product-carousel__container">
        <div v-for="(product, index) in products" :key="product.id" class="product-carousel__slide">
          <ProductCard
            :product="product"
            :item-list-id="props.itemListId"
            :item-list-name="props.itemListName"
            :index="index"
          />
        </div>
      </div>
    </div>

    <button
      class="carousel-arrow carousel-arrow--next"
      type="button"
      aria-label="Następne"
      @click="scrollByPage('next')"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </button>
  </div>
</template>

<style scoped>
.product-scroll-carousel {
  position: relative;
}

.product-carousel__viewport {
  overflow: hidden;
  padding-left: calc((100vw - var(--container)) / 2);
}

.product-carousel__container {
  display: flex;
  gap: 4px;
  touch-action: pan-y pinch-zoom;
}

.product-carousel__slide {
  flex: 0 0 calc((var(--container) - 8px) / 3);
  min-width: 0;
}

/* Proles (04.11.2026): 4 pełne kafle + zajawka piątego zamiast 3 + zajawki
   czwartego — jak listingi proles, 4 w rzędzie od 1200 px. 12px = 3 odstępy.
   Kafel ma wtedy szerokość kafla siatki na stronie głównej, więc rezerwa
   paska karty z themes.css pasuje bez zmian. Pozostałe marki jak dotąd. */
@media (min-width: 1200px) {
  [data-brand="proles"] .product-carousel__slide {
    flex-basis: calc((var(--container) - 12px) / 4);
  }
}

.product-carousel__viewport :deep(a),
.product-carousel__viewport :deep(img) {
  -webkit-user-drag: none;
}

.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  width: 44px;
  height: 44px;
  background: var(--color-white);
  border: 1px solid var(--color-border);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-black);
  transition: background var(--transition-fast);
  border-radius: 0;
}

.carousel-arrow:hover {
  background: var(--color-bg);
}

.carousel-arrow--prev {
  left: calc((100vw - var(--container)) / 2);
}

.carousel-arrow--next {
  right: 0;
}

@media (max-width: 960px) {
  .product-carousel__slide {
    flex-basis: calc((var(--container) - 4px) / 2);
  }
}
</style>
